import { AnimatePresence, motion } from 'framer-motion'
import type { FC } from 'react'
import { useState, useRef } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import 'swiper/css/scrollbar'
import { Scrollbar, Navigation, Controller } from 'swiper/modules'
import { Swiper } from 'swiper/react'

import { IconArrow } from 'components/icons/arrow'
import { Section } from 'components/section'
import { useMenuItem } from 'hooks/useMenuItem'
import type { dataType } from 'models'
import { descriptionVariants } from 'styles/variants'

import { ModalGallery } from './modal'
import {
  Container,
  SwiperContainer,
  SwiperScrollbar,
  SwiperNav,
  SwiperText,
  SliderImage,
  Slide,
  IconPlay,
  Soon,
} from './styled'

type NavOpts = {
  prevEl?: HTMLElement | null
  nextEl?: HTMLElement | null
}

export const Gallery: FC<{
  readonly type: string
  readonly title: string
  readonly data: dataType[]
  readonly slidePerView: boolean
}> = ({ type, title, data, slidePerView }) => {
  const ref = useMenuItem()
  const swiperRef = useRef<SwiperType | null>(null)
  const swiperPrevRef = useRef(null)
  const swiperNextRef = useRef(null)
  const swiperScrollbarRef = useRef(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [displayIndex, setDisplayIndex] = useState(0)
  const [direction, setDirection] = useState(-1)
  const prevIndexRef = useRef(0)
  const lastInteractionRef = useRef<'next' | 'prev' | null>(null)
  const [modalOpened, setModalOpened] = useState(false)
  const [youtubePlaying, setYoutubePlaying] = useState(-1)

  const closeModal = () => {
    setModalOpened(false)
    document.body.style.overflow = ''
  }
  const openModal = (index: number) => {
    const isActive = swiperRef.current?.activeIndex === index

    if (type === 'image') {
      if (isActive) {
        setModalOpened(true)
        document.body.style.overflow = 'hidden'
      } else {
        swiperRef.current?.slideTo(index)
      }
    }
  }

  const handleSwipeChange = (swiper: SwiperType) => {
    const newIndex = swiper.realIndex ?? swiper.activeIndex

    setYoutubePlaying(-1)
    let dir: 1 | -1
    if (swiper.swipeDirection) {
      dir = swiper.swipeDirection === 'next' ? 1 : -1
      lastInteractionRef.current = swiper.swipeDirection
    } else {
      const last = lastInteractionRef.current
      if (last === 'next') dir = 1
      else if (last === 'prev') dir = -1
      else dir = newIndex > prevIndexRef.current ? 1 : -1
    }

    prevIndexRef.current = newIndex
    setDirection(dir)
    setActiveIndex(newIndex)
  }

  const handleTransitionEnd = (swiper: SwiperType) => {
    const newIndex = swiper.realIndex ?? swiper.activeIndex
    setDisplayIndex(newIndex)
  }

  return (
    <>
      <Section ref={ref} data-menu-label={title} title={title}>
        <Container>
          <SwiperContainer $slidePerView={slidePerView} $type={type}>
            <Swiper
              onSwiper={swiper => (swiperRef.current = swiper)}
              slidesPerView={slidePerView ? 'auto' : 1}
              centeredSlides
              scrollbar={{ el: swiperScrollbarRef.current }}
              modules={[Scrollbar, Navigation, Controller]}
              navigation={{
                prevEl: swiperPrevRef.current,
                nextEl: swiperNextRef.current,
              }}
              onBeforeInit={(swiper: SwiperType) => {
                const navigation = swiper.params.navigation as NavOpts
                navigation.prevEl = swiperPrevRef.current
                navigation.nextEl = swiperNextRef.current
              }}
              onSlideChange={handleSwipeChange}
              onSlideChangeTransitionEnd={handleTransitionEnd}
            >
              {data.map((slide, index) => (
                <Slide
                  key={slide.id}
                  $isActive={activeIndex === index}
                  onClick={() => {
                    type === 'image' && openModal(index)
                    type === 'video' &&
                      slide.youtube &&
                      setYoutubePlaying(index)
                  }}
                >
                  <SliderImage
                    $mode={slidePerView ? 'width' : 'height'}
                    alt={type === 'image' ? 'gallery' : 'video'}
                    src={slide.img}
                  />

                  {type !== 'image' && slide.youtube && <IconPlay />}
                  {type !== 'image' && !slide.youtube && <Soon>скоро</Soon>}
                  {type !== 'image' && youtubePlaying === index && (
                    <iframe
                      width="560"
                      height="315"
                      src={
                        'https://www.youtube.com/embed/' +
                        slide.youtube +
                        '?autoplay=1'
                      }
                      title="YouTube video player"
                      frameBorder="0"
                      allow="autoplay; encrypted-media"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                  )}
                </Slide>
              ))}
            </Swiper>
          </SwiperContainer>

          <SwiperNav
            ref={swiperPrevRef}
            $align="left"
            $position={type === 'video' ? 'near' : 'far'}
            onClick={() => {
              setDirection(-1)
              setYoutubePlaying(-1)
            }}
          >
            <IconArrow aria-label="Previous" />
          </SwiperNav>

          <SwiperNav
            ref={swiperNextRef}
            $align="right"
            $position={type === 'video' ? 'near' : 'far'}
            onClick={() => {
              setDirection(1)
              setYoutubePlaying(-1)
            }}
          >
            <IconArrow aria-label="Next" />
          </SwiperNav>

          <SwiperScrollbar
            ref={swiperScrollbarRef}
            className="swiper-scrollbar"
          />
        </Container>

        <SwiperText>
          <AnimatePresence
            mode="popLayout"
            initial={false}
            presenceAffectsLayout
            custom={direction}
          >
            <motion.div
              key={displayIndex}
              variants={descriptionVariants}
              initial="initial"
              animate="visible"
              exit="hidden"
              custom={direction}
            >
              {data[displayIndex].text}
            </motion.div>
          </AnimatePresence>
        </SwiperText>
      </Section>

      {type === 'image' && modalOpened && (
        <ModalGallery
          data={data}
          activeSlide={activeIndex}
          onClose={closeModal}
          parentSwiper={swiperRef}
        />
      )}
    </>
  )
}
